import { useState, useCallback, useRef } from 'react';
import { condoService, CondoLocationFilters } from '../services/condoService';
import {
  Condo,
  CondoPagination,
  GetWaitlistUsersResponse,
} from '../types/condo';

export const useCondos = () => {
  const [condos, setCondos] = useState<Condo[]>([]);
  const [pagination, setPagination] = useState<CondoPagination>({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store active filters so goToPage can reuse them without triggering re-renders
  const activeFiltersRef = useRef<CondoLocationFilters | undefined>(undefined);

  const fetchCondos = useCallback(async (
    page: number = 1,
    limit: number = 20,
    filters?: CondoLocationFilters
  ) => {
    // Persist filters so goToPage can forward them on subsequent page changes
    activeFiltersRef.current = filters;
    setLoading(true);
    setError(null);
    try {
      const res = await condoService.listCondos(page, limit, filters);
      if (res.success && res.data) {
        setCondos(res.data.condos ?? []);
        setPagination(res.data.pagination ?? { page: 1, limit: 20, total: 0 });
      } else {
        setCondos([]);
        setError('Failed to load condos');
      }
    } catch (err) {
      setError((err as Error).message);
      setCondos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const joinWaitlist = useCallback(
    async (condoId: string) => {
      try {
        const res = await condoService.joinWaitlist(condoId);
        if (res.success && res.data) {
          setCondos((prev) =>
            prev.map((c) =>
              (c._id ?? c.id) === condoId
                ? {
                    ...c,
                    isOnWaitlist: true,
                    waitlistCount: (c.waitlistCount ?? 0) + 1,
                  }
                : c
            )
          );
          return { success: true };
        }
        return { success: false, error: 'Failed to join waitlist' };
      } catch (err) {
        return { success: false, error: (err as Error).message };
      }
    },
    []
  );

  const leaveWaitlist = useCallback(async (condoId: string) => {
    try {
      const res = await condoService.leaveWaitlist(condoId);
      if (res.success && res.data) {
        setCondos((prev) =>
          prev.map((c) =>
            (c._id ?? c.id) === condoId
              ? {
                  ...c,
                  isOnWaitlist: false,
                  waitlistCount: Math.max(0, (c.waitlistCount ?? 1) - 1),
                }
              : c
          )
        );
        return { success: true };
      }
      return { success: false, error: 'Failed to leave waitlist' };
    } catch (err) {
      return { success: false, error: (err as Error).message };
    }
  }, []);

  const getWaitlistUsers = useCallback(async (
    condoId: string
  ): Promise<{ success: boolean; data?: GetWaitlistUsersResponse; error?: string }> => {
    try {
      const res = await condoService.getWaitlistUsers(condoId);
      if (res.success && res.data) {
        return { success: true, data: res.data };
      }
      return { success: false, error: res.message ?? 'Failed to get waitlist users' };
    } catch (err) {
      return { success: false, error: (err as Error).message };
    }
  }, []);

  const setCondoIsOnWaitlist = useCallback((condoId: string, isOnWaitlist: boolean) => {
    setCondos((prev) =>
      prev.map((c) =>
        (c._id ?? c.id) === condoId ? { ...c, isOnWaitlist } : c
      )
    );
  }, []);

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= Math.ceil(pagination.total / pagination.limit)) {
        fetchCondos(page, pagination.limit, activeFiltersRef.current);
      }
    },
    [fetchCondos, pagination.total, pagination.limit]
  );

  return {
    condos,
    pagination,
    loading,
    error,
    fetchCondos,
    joinWaitlist,
    leaveWaitlist,
    getWaitlistUsers,
    setCondoIsOnWaitlist,
    goToPage,
  };
};
